import React from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { ValueOf } from 'type-fest';
import { Div, BoxProps } from '../../box';
import { TextareaAutogrowContext } from './textarea-autogrow-context';
import { TextareaAutogrowSizes } from './style';

interface TextareaAutogrowContainerProps {
  className?: string;
  children: React.ReactNode;
  size?: keyof typeof TextareaAutogrowSizes;
  borderRadius?: string;
}

const StyledContainer = styled.div<{
  $isExpanded: boolean;
  $maxHeight?: number;
  $borderRadius?: string;
  $size: ValueOf<typeof TextareaAutogrowSizes>;
}>`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: ${({ $isExpanded }) => ($isExpanded ? 'auto auto' : 'auto')};
  grid-template-areas: ${({ $isExpanded }) =>
    $isExpanded ? '"textarea textarea" "button button"' : '"textarea button"'};
  align-items: ${({ $isExpanded }) => ($isExpanded ? 'stretch' : 'center')};

  /* Inherit input styling */
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: ${({ $size }) => $size?.borderRadius || '4px'};
  transition: border-color 0.2s ease;
  overflow-y: hidden;

  &:focus-within {
    border-color: ${props => props.theme.actionColor};
  }

  div.textarea-autogrow-button-container {
    grid-area: button;
    display: flex;
    align-items: center;
    justify-content: ${({ $isExpanded }) => ($isExpanded ? 'flex-end' : 'center')};
    padding: ${({ $isExpanded }) => ($isExpanded ? '0 8px 8px 8px' : '0 8px 0 0')};
  }

  textarea.textarea-autogrow-input {
    grid-area: textarea;
    max-height: ${({ $maxHeight }) => `${$maxHeight}px`};
    padding: ${({ $size }) => $size.padding ?? '14px 16px'};

    /* Remove default input styling since container now has it */
    background: transparent;
    border: none;
    box-shadow: none;
    border-radius: inherit;

    &:focus {
      border: none;
      box-shadow: none;
      outline: none;
    }
  }
`;

const ButtonContainer: React.FC<BoxProps> = ({ className, children, ...rest }) => (
  <Div className={classNames('textarea-autogrow-button-container', className)} {...rest}>
    {children}
  </Div>
);

const TextareaAutogrowContainer: React.FC<TextareaAutogrowContainerProps> & {
  ButtonContainer: typeof ButtonContainer;
} = ({ children, className, size = 'xl' }: TextareaAutogrowContainerProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    <TextareaAutogrowContext value={{ isExpanded, setIsExpanded }}>
      <StyledContainer $isExpanded={isExpanded} $size={TextareaAutogrowSizes[size]} className={className}>
        {children}
      </StyledContainer>
    </TextareaAutogrowContext>
  );
};

TextareaAutogrowContainer.ButtonContainer = ButtonContainer;

export default TextareaAutogrowContainer;
